import {
  Box,
  Button,
  Flex,
  Heading,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';

import { finleyAssert } from '@finley-technologies/common';

import { Deliverables } from '../CreditAgreementFullArtifact/section/Deliverables';
import {
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { useGetCreditAgreementFullArtifactQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { BorrowingBaseMechanics } from 'packages/creditAgreements/ui/CreditAgreementArtifact/sections/BorrowingBaseMechanics';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { CreditAgreementContextProvider } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/hooks';
import { FacilityMechanics } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/section/FacilityMechanics';
import { FinancialCovenants } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/section/FinancialCovenants';
import { FundingRequestMechanics } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/section/FundingRequestMechanics';
import { PortfolioCovenants } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/section/PortfolioCovenants';
import { SettlementMechanics } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/section/SettlementMechanics';
import { FiArrowLeft } from 'react-icons/fi';
import { z } from 'zod/v4';
import { useCreditFacilityOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { CreditFacilityDocumentsHistoryAndUploadContainer } from '~/packages/credit-facility/ui/DocumentUpload/CreditFacilityDocumentsHistoryAndUploadContainer';

// Types
const RouteParamsStruct = z.object({
  creditAgreementId: z.string({ message: 'Missing URL parameter' }),
});

// Fragments
type CreditAgreementViewHeaderProps = {
  creditFacilityId: string;
};
const CreditAgreementViewHeader = ({
  creditFacilityId,
}: CreditAgreementViewHeaderProps) => {
  const creditFacility = useCreditFacilityOrThrow(creditFacilityId);
  return (
    <Flex mt="30px">
      <Button as={RouterLink} variant="ghost" to="/credit-agreements/">
        <FiArrowLeft />
      </Button>
      <Box>
        <Heading as="h2">Credit Agreement</Heading>
        <Text mt="10px">{creditFacility.name}</Text>
      </Box>
    </Flex>
  );
};

// Exports
export const CreditAgreement = ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const params = useParams();
  const { creditAgreementId } = RouteParamsStruct.parse(params);

  const toast = useToast();
  const navigate = useNavigate();

  const {
    data: creditAgreementData,
    loading: creditAgreementLoading,
  } = useGetCreditAgreementFullArtifactQuery({
    variables: { creditAgreementId },
    onError: () => {
      if (!toast.isActive(errorToastId)) {
        errorToastConfig.title = `Error loading credit agreement.`;
        toast(errorToastConfig);
      }
    },
    fetchPolicy: 'network-only',
  });

  if (
    creditAgreementLoading ||
    !creditAgreementData ||
    !creditAgreementData.creditAgreementFullArtifact
  ) {
    return (
      <Skeleton height="600px" data-testid="credit-agreement-route-loading" />
    );
  }

  const {
    creditAgreementFullArtifact: { creditAgreement, id },
  } = creditAgreementData;

  let parsedCreditAgreement = null;

  try {
    parsedCreditAgreement = parseCreditAgreementFullArtifact(creditAgreement);
    finleyAssert(
      parsedCreditAgreement,
      `Expect creditAgreementFullArtifact to exist for creditAgreementId ${id}`,
    );

    const {
      borrowingBaseMechanics,
      creditFacilityId,
      fundingRequestMechanics,
      facilityMechanics,
      settlementMechanics,
      financialCovenants,
      portfolioCovenants,
      deliverables,
    } = parsedCreditAgreement;

    let routeLevelError = false;
    return (
      <Box>
        <Sentry.ErrorBoundary
          onError={() => {
            if (!routeLevelError && !toast.isActive(errorToastId)) {
              errorToastConfig.title = `Failed to load credit agreement page`;
              routeLevelError = true;
              toast(errorToastConfig);
              navigate('/credit-agreements');
            }
          }}
        >
          <Box>
            <CreditAgreementViewHeader creditFacilityId={creditFacilityId} />
            <Tabs>
              <TabList>
                <Tab>Overview</Tab>
                <Tab>Documents</Tab>
              </TabList>

              <TabPanels>
                <TabPanel>
                  <FacilityMechanics facilityMechanics={facilityMechanics} />
                  <CreditAgreementContextProvider
                    initOrgSlugs={[]}
                    parties={facilityMechanics.parties}
                  >
                    <FundingRequestMechanics
                      fundingRequestMechanics={fundingRequestMechanics}
                    />
                  </CreditAgreementContextProvider>
                  <SettlementMechanics
                    settlementMechanics={settlementMechanics}
                  />
                  <BorrowingBaseMechanics
                    borrowingBaseMechanics={borrowingBaseMechanics}
                  />
                  <FinancialCovenants
                    financialCovenants={financialCovenants}
                    organizationId={organizationId}
                  />
                  <PortfolioCovenants
                    portfolioCovenants={portfolioCovenants}
                    organizationId={organizationId}
                  />
                  {/* Deliverable v4 */}
                  <Deliverables deliverables={deliverables} />
                </TabPanel>
                <TabPanel>
                  <CreditFacilityDocumentsHistoryAndUploadContainer
                    creditFacilityId={creditFacilityId}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </Sentry.ErrorBoundary>
      </Box>
    );
  } catch (err) {
    errorToastConfig.title = `Error parsing credit agreement artifact for credit agreement ${id}`;
    toast(errorToastConfig);
    navigate('/credit-agreements');
  }
  throw new Error('unreachable code to make linter happy');
};
